import { Text } from "@/components/ui/text";
import { Pressable } from "@/components/ui/pressable";
import { IconMC } from "@/components/media/IconsV2";
import i18n from "i18n-js";
import { createStackNavigator } from '@react-navigation/stack';
import MainStack from '../../../containers/MainStack';
import React, { useEffect, useRef } from 'react';
import { Animated, PanResponder, View } from 'react-native';
import { taskDelete, taskDone } from "@/stores/mainSlice";
import { useDispatch, useSelector } from 'react-redux'
import OpenIssueButton from "@/components/basic/OpenIssueButton";
import { Box } from "@/components/ui/box";

const appStack = createStackNavigator();
const EDITOR_HEADER_HEIGHT = 60;

export default function ModalSimulator({ route, navigation }){
    const { taskid } = route.params
    const dispatch = useDispatch()
    const pan = useRef(new Animated.ValueXY()).current;
    const panPosition = useRef({ x: 0, y: 0 });
    const panStartPosition = useRef({ x: 0, y: 0 });
    const layout = useRef({ containerWidth: 0, containerHeight: 0, simulatorWidth: 0, simulatorHeaderHeight: 0 });
    const internal = useSelector((state) => state.settings.preview)

    // Only the header acts as a drag handle. Otherwise the responder would
    // swallow gestures intended for the navigator and its screens.
    const panResponder = useRef(
        PanResponder.create({
            onStartShouldSetPanResponder: () => false,
            onMoveShouldSetPanResponder: (_, gestureState) =>
                Math.abs(gestureState.dx) > 3 || Math.abs(gestureState.dy) > 3,
            onMoveShouldSetPanResponderCapture: (_, gestureState) =>
                Math.abs(gestureState.dx) > 3 || Math.abs(gestureState.dy) > 3,
            onPanResponderGrant: () => {
                panStartPosition.current = { ...panPosition.current }
            },
            onPanResponderMove: (_, gestureState) => {
                const { containerWidth, containerHeight, simulatorWidth, simulatorHeaderHeight } = layout.current
                const horizontalMargin = Math.max(0, (containerWidth - simulatorWidth) / 2)
                const minimumX = -horizontalMargin
                const maximumX = horizontalMargin
                const maximumY = Math.max(0, containerHeight - EDITOR_HEADER_HEIGHT - simulatorHeaderHeight)
                const requestedX = panStartPosition.current.x + gestureState.dx
                const requestedY = panStartPosition.current.y + gestureState.dy
                const nextPosition = {
                    x: Math.min(maximumX, Math.max(minimumX, requestedX)),
                    y: Math.min(maximumY, Math.max(0, requestedY)),
                }

                panPosition.current = nextPosition
                pan.setValue(nextPosition)
            },
        })
    ).current;

    useEffect(() => {
        const unsubscribeBeforeRemove = navigation.addListener('beforeRemove', () => {
            // Stop timers and remove the preview from the active task lists
            // while its screens still have access to the workflow.
            dispatch(taskDone({ taskid, abort: true }))
        })
        const unsubscribeTransitionEnd = navigation.addListener('transitionEnd', (event) => {
            if (event.data.closing) {
                // Only remove the workflow after all simulator screens have
                // finished rendering their closing transition.
                dispatch(taskDelete({ taskid }))
            }
        })

        return () => {
            unsubscribeBeforeRemove()
            unsubscribeTransitionEnd()
        }
    }, [dispatch, navigation, taskid])

    const closeSimulator = () => {
        navigation.goBack()
    }

    return (
        <View className="flex-1 items-center pt-[60px]" pointerEvents="box-none"
            onLayout={(event) => {
                const { width, height } = event.nativeEvent.layout
                layout.current.containerWidth = width
                layout.current.containerHeight = height
            }}
        >
            <Animated.View style={{ flex: 1, width: '100%', maxWidth: 390, maxHeight: 844, transform: pan.getTranslateTransform() }}
                onLayout={(event) => {
                    layout.current.simulatorWidth = event.nativeEvent.layout.width
                }}
            >
                <View className="flex-1 bg-secondary-50 border border-primary-900 shadow-hard-3">
                    <View className="relative flex-row items-center cursor-move select-none"
                        onLayout={(event) => {
                            layout.current.simulatorHeaderHeight = event.nativeEvent.layout.height
                        }}
                        {...panResponder.panHandlers}
                    >
                        <Text className='p-5 text-lg font-semibold text-black'>{i18n.t('studio.flow.simulator')}</Text>
                        {internal && <Box className={`mr-4 border-[2px] border-white bg-white rounded-full mt-1 `}>
                            <OpenIssueButton navigation={navigation} icon='bug' />
                        </Box>}
                        <Pressable className="absolute top-3 right-3 z-10 hover:opacity-50" onPress={() => closeSimulator()}>
                            <View className='bg-primary-50 border-2 border-primary-900 rounded-[4px] p-1 w-fit self-end'>
                                <IconMC className='text-primary-900' size={24} name='close' />
                            </View>
                        </Pressable>
                    </View>
                    <View className="flex-1 min-h-0">
                        <appStack.Navigator initialRouteName="MainStack">
                            <appStack.Screen name="MainStack" options={{ headerShown: false }}>
                                {(props) => <MainStack {...props} isSimulator />}
                            </appStack.Screen>
                        </appStack.Navigator>
                    </View>
                </View>
            </Animated.View>
        </View>
    );
}
